import { createContext, useContext, useState, useCallback, useRef } from 'react';
import ModaleAnimee from "../Components/ModaleAnimee";

const ConfirmContext = createContext(null);

/**
 * Remplace window.confirm / window.alert / window.prompt par des modals cohérents avec le
 * reste de l'app (les dialogues natifs du navigateur ne peuvent pas être stylés et cassent le
 * design partout où ils apparaissent).
 *
 * const confirm = useConfirm(); if (!(await confirm('Supprimer ?'))) return;
 * const alerte  = useAlerte();  await alerte('Enregistré avec succès.');
 * const prompt  = usePrompt();  const val = await prompt('Résolution :'); if (val) ...
 *
 * Options communes : { titre, danger, confirmLabel, cancelLabel }. `prompt` accepte en plus
 * { valeurInitiale, placeholder }.
 */
export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { mode: 'confirm'|'alerte'|'prompt', message, titre, danger, confirmLabel, cancelLabel, valeur, resolve }

  const confirm = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'confirm', message,
        titre:        opts.titre || 'Confirmer l\'action',
        danger:       opts.danger !== false,
        confirmLabel: opts.confirmLabel || 'Confirmer',
        cancelLabel:  opts.cancelLabel || 'Annuler',
        resolve,
      });
    });
  }, []);

  const alerte = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'alerte', message,
        titre:        opts.titre || 'Information',
        danger:       opts.danger === true,
        confirmLabel: opts.confirmLabel || 'OK',
        resolve,
      });
    });
  }, []);

  const prompt = useCallback((message, opts = {}) => {
    return new Promise((resolve) => {
      setState({
        mode: 'prompt', message,
        titre:        opts.titre || 'Saisie requise',
        confirmLabel: opts.confirmLabel || 'Valider',
        cancelLabel:  opts.cancelLabel || 'Annuler',
        placeholder:  opts.placeholder || '',
        valeur:       opts.valeurInitiale || '',
        resolve,
      });
    });
  }, []);

  function repondre(valeur) {
    state?.resolve(valeur);
    setState(null);
  }

  /* LE DERNIER CONTENU EST RETENU LE TEMPS DE LA SORTIE.
     `AnimatePresence` garde la fenêtre montée pendant son animation de fermeture, mais
     `state` est déjà passé à `null` : lire `state.titre` pendant ces 140 ms afficherait une
     boîte vide qui se referme — pire que pas d'animation du tout. On rend donc le dernier
     contenu connu, et c'est `state` seul qui commande l'ouverture. */
  const dernier = useRef(null);
  if (state) dernier.current = state;
  const vu = state || dernier.current;

  return (
    <ConfirmContext.Provider value={{ confirm, alerte, prompt }}>
      {children}
      <ModaleAnimee
        ouvert={!!state}
        onFermer={() => repondre(vu?.mode === 'prompt' ? null : false)}
        style={{ position: 'fixed', inset: 0, background: 'rgba(10,18,35,0.6)', zIndex: 100000, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(3px)' }}
        panneauStyle={{ background: '#fff', borderRadius: 16, padding: '26px 28px', maxWidth: 420, width: '90vw', boxShadow: '0 30px 80px rgba(0,0,0,0.3)', fontFamily: 'inherit' }}
      >
        {vu && (
          <>
            <div style={{ fontSize: 16, fontWeight: 800, color: vu.danger ? '#991B1B' : '#0B1220', marginBottom: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
              {vu.danger && <span style={{ fontSize: 18 }}>⚠️</span>}
              {vu.titre}
            </div>
            <div style={{ fontSize: 13.5, color: '#4B5563', lineHeight: 1.6, marginBottom: vu.mode === 'prompt' ? 12 : 22, whiteSpace: 'pre-line' }}>
              {vu.message}
            </div>
            {vu.mode === 'prompt' && (
              <input
                autoFocus
                type="text"
                value={vu.valeur}
                placeholder={vu.placeholder}
                onChange={e => setState(s => ({ ...s, valeur: e.target.value }))}
                onKeyDown={e => { if (e.key === 'Enter' && vu.valeur.trim()) repondre(vu.valeur); if (e.key === 'Escape') repondre(null); }}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px', borderRadius: 9, border: '1.5px solid #e5e7eb', fontSize: 13, fontFamily: 'inherit', outline: 'none', marginBottom: 22 }}
              />
            )}
            <div style={{ display: 'flex', gap: 10 }}>
              {vu.mode !== 'alerte' && (
                <button
                  onClick={() => repondre(vu.mode === 'prompt' ? null : false)}
                  style={{ flex: 1, padding: '10px', borderRadius: 9, border: '1px solid #e5e7eb', background: '#f9fafb', color: '#374151', fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}
                >
                  {vu.cancelLabel}
                </button>
              )}
              <button
                onClick={() => repondre(vu.mode === 'prompt' ? vu.valeur : true)}
                disabled={vu.mode === 'prompt' && !vu.valeur.trim()}
                autoFocus={vu.mode !== 'prompt'}
                style={{ flex: 1, padding: '10px', borderRadius: 9, border: 'none', background: (vu.mode === 'prompt' && !vu.valeur.trim()) ? '#93c5fd' : (vu.danger ? '#dc2626' : '#2563eb'), color: '#fff', fontSize: 13, fontWeight: 700, cursor: (vu.mode === 'prompt' && !vu.valeur.trim()) ? 'not-allowed' : 'pointer', fontFamily: 'inherit' }}
              >
                {vu.confirmLabel}
              </button>
            </div>
          </>
        )}
      </ModaleAnimee>
    </ConfirmContext.Provider>
  );
}

function useConfirmContext() {
  const ctx = useContext(ConfirmContext);
  if (!ctx) throw new Error('useConfirm/useAlerte/usePrompt doivent être utilisés sous <ConfirmProvider>');
  return ctx;
}

export function useConfirm() { return useConfirmContext().confirm; }
export function useAlerte()  { return useConfirmContext().alerte; }
export function usePrompt()  { return useConfirmContext().prompt; }
